import React from 'react';
import { useIntl } from 'react-intl';

import { FieldTextInput } from '../../components';
import { validateVAT } from '../../util/api';
import { minLength } from '../../util/validators';

const EU_COUNTRY_CODES = [
  'AT', 'BE', 'BG', 'HR', 'CY', 'CZ', 'DK', 'EE', 'FI', 'FR',
  'DE', 'EL', 'HU', 'IE', 'IT', 'LV', 'LT', 'LU', 'MT', 'NL',
  'PL', 'PT', 'RO', 'SK', 'SI', 'ES', 'SE',
];

const validateVATNumber = async (value, intl, onVatVerified) => {
  if (!value) return undefined;

  const countryCode = value?.substring?.(0, 2)?.toUpperCase();
  const vatNumber = value?.substring?.(2);

  const invalidLength = minLength(
    intl.formatMessage({ id: 'FieldVATInput.minLengthError' }),
    10
  )(value);

  if (invalidLength) {
    if (onVatVerified) onVatVerified(false);
    return invalidLength;
  }

  if (!EU_COUNTRY_CODES.includes(countryCode)) {
    if (onVatVerified) onVatVerified(false);
    return intl.formatMessage({ id: 'FieldVATInput.invalidCountryCode' });
  }

  if (!vatNumber || !countryCode) return undefined;

  try {
    const response = await validateVAT({ countryCode, vatNumber });
    if (onVatVerified) onVatVerified(response.valid);
    return response.valid ? undefined : intl.formatMessage({ id: 'FieldVATInput.invalidVAT' });
  } catch (error) {
    return intl.formatMessage({ id: 'FieldVATInput.validationError' });
  }
};

/**
 * FieldVATInput — EU VAT number input with real-time VIES validation.
 *
 * Format: {2-letter country code}{VAT number}, e.g. "DE123456789"
 *
 * @param {Function} onVatVerified - callback(isValid: boolean) called after validation
 */
const FieldVATInput = props => {
  const intl = useIntl();
  const { onVatVerified, ...restProps } = props;

  return (
    <FieldTextInput
      {...restProps}
      type="text"
      validate={value => validateVATNumber(value, intl, onVatVerified)}
    />
  );
};

export default FieldVATInput;
